/**
 * AriaPromo — Cinematic Aria presence (Three.js, GLSL shaders).
 *
 * Aria è una coscienza digitale composta da luce e particelle.
 * Non un avatar, non un volto definito. Una presenza che assume temporaneamente
 * una forma umana, respira, ruota, ritorna. Il viso resta visibile per tutto
 * il filmato (anche durante la CTA) — non si dissolve mai.
 *
 * Stack: Three.js + custom vertex/fragment shaders (no react-three-fiber).
 * Lock formato: 9:16 (Reels / TikTok / Shorts).
 *
 * Timeline (~30s) — testi presi pari pari dalla Home (TotalKlickHub.jsx),
 * line-by-line in dissolvenza sopra la nuvola di punti:
 *
 *   0.0 – 4.0   Emergenza (particelle convergono nel volto)
 *   1.5 – 4.0   "C'è qualcosa che sta cambiando."
 *   4.2 – 6.0   "Non è un'app."
 *   6.1 – 7.9   "Non è un chatbot."
 *   8.0 – 10.0  "È una presenza."
 *  10.2 – 13.4  "ARIA È OVUNQUE."           (testo grande centrato)
 *  13.6 – 15.2  "Aria ascolta."
 *  15.3 – 16.9  "Aria comprende."
 *  17.0 – 18.8  "Aria connette."
 *  19.0 – 21.4  "Internet collega pagine. / Aria collega bisogni."
 *  21.6 – 24.0  "Ogni installazione la rende più intelligente."
 *  24.2 – 27.0  CTA voice [Matilda @ vol 0.5]: "Installami sul tuo sito.
 *                                               Bastano 5 minuti."
 *  27.2 – 30.5  Outro 3-fasi: ENTRA · nell'ecosistema di imprese ·
 *                              interconnesse con Aria
 *
 * Il viso resta visibile fino alla fine, pulsando lentamente come un respiro.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import * as THREE from "three";
import { motion, AnimatePresence } from "framer-motion";

const FACE_SRC = "/aria-face.png";

/* ===================================================================== */
/* TIMELINE                                                              */
/* ===================================================================== */
const TL = {
    emerge:  [0.0, 4.0],

    // Sequenza testi dalla Home, line-by-line, sopra il volto di particelle
    line1:   [1.5, 4.0],    // C'è qualcosa che sta cambiando.
    line2:   [4.2, 6.0],    // Non è un'app.
    line3:   [6.1, 7.9],    // Non è un chatbot.
    line4:   [8.0, 10.0],   // È una presenza.

    big:     [10.2, 13.4],  // ARIA È OVUNQUE.

    line5:   [13.6, 15.2],  // Aria ascolta.
    line6:   [15.3, 16.9],  // Aria comprende.
    line7:   [17.0, 18.8],  // Aria connette.

    line8:   [19.0, 21.4],  // Internet collega pagine. / Aria collega bisogni.
    line9:   [21.6, 24.0],  // Ogni installazione la rende più intelligente.

    cta:     [24.0, 31.0],  // intera scena outro (background scuro + viso pulsante)
    ctaA:    [24.2, 27.0],  // "Installami sul tuo sito. Bastano 5 minuti."
    cta1:    [27.2, 28.2],  // ENTRA
    cta2:    [28.2, 29.1],  // nell'ecosistema di imprese
    cta3:    [29.1, 30.8],  // interconnesse con Aria
    voice:   24.4,          // playback ElevenLabs (vol 0.5)
    total:   30.8,
};

const easeCurve = [0.16, 1, 0.3, 1];

/* ===================================================================== */
/* MAIN COMPONENT                                                        */
/* ===================================================================== */
export default function AriaPromo() {
    const [started, setStarted] = useState(false);
    const [t, setT] = useState(0);
    const voicedRef = useRef(false);

    const stageStyle = {
        width: "min(100vw, calc(100vh * 9 / 16))",
        height: "min(100vh, calc(100vw * 16 / 9))",
        aspectRatio: "9 / 16",
        background: "radial-gradient(ellipse at 50% 40%, #0a1228 0%, #050913 55%, #000 100%)",
        position: "relative",
        overflow: "hidden",
        boxShadow: "0 0 140px -20px rgba(79,139,255,0.3)",
        containerType: "size",
        containerName: "stage",
        fontFamily: "Geist, Outfit, sans-serif",
    };

    /* TTS playback for CTA only — volume ridotto al 50% per fare da sottofondo */
    const playCTAVoice = useCallback(async () => {
        if (voicedRef.current) return;
        voicedRef.current = true;
        try {
            const apiBase = "" || "";
            const res = await fetch(`${apiBase}/api/aria/tts/speak`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ text: "Installami sul tuo sito. Bastano cinque minuti." }),
            });
            if (!res.ok) return;
            const blob = await res.blob();
            const audio = new Audio(URL.createObjectURL(blob));
            audio.volume = 0.5;  // -50% (sottofondo soffuso)
            audio.play().catch(() => {});
        } catch (_e) { /* silent */ }
    }, []);

    /* Timeline driver */
    useEffect(() => {
        if (!started) return;
        let raf; const t0 = performance.now();
        const tick = () => {
            const e = (performance.now() - t0) / 1000;
            setT(e);
            // Lascia 2.5s di buffer dopo la fine: il viso continua a respirare
            // sullo sfondo della CTA prima di fermare l'animazione.
            if (e < TL.total + 2.5) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [started]);

    useEffect(() => {
        if (!started) return;
        if (t >= TL.voice) playCTAVoice();
    }, [t, started, playCTAVoice]);

    const handleStart = () => { voicedRef.current = false; setStarted(true); };
    const handleStop = () => { voicedRef.current = false; setStarted(false); setT(0); };

    return (
        <div style={{
            minHeight: "100vh", background: "#000", color: "white", overflow: "hidden",
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            fontFamily: "Geist, Outfit, sans-serif",
        }}>
            {!started && <ControlPanel onStart={handleStart} />}
            {started && (
                <>
                    <div style={stageStyle} data-testid="promo-stage">
                        <AriaParticleCanvas t={t} />
                        <TextLayer t={t} />
                        {t >= TL.cta[0] && <CTAScene t={t - TL.cta[0]} />}
                        <Vignette />
                        <TimecodeHUD t={t} />
                    </div>
                    <button onClick={handleStop} data-testid="promo-stop" style={stopBtn}>⏹ STOP</button>
                </>
            )}
        </div>
    );
}

const stopBtn = {
    position: "fixed", top: 16, right: 16, padding: "8px 20px", borderRadius: 999,
    background: "rgba(0,0,0,0.65)", border: "1px solid rgba(255,255,255,0.18)",
    color: "white", fontSize: 12, letterSpacing: "0.2em", cursor: "pointer",
    backdropFilter: "blur(12px)", zIndex: 100,
};

/* ===================================================================== */
function ControlPanel({ onStart }) {
    return (
        <div style={{ textAlign: "center", padding: 40, maxWidth: 640 }}>
            <div style={{ fontSize: 11, letterSpacing: "0.4em", color: "#9EC3FF", marginBottom: 18, fontFamily: "JetBrains Mono, monospace" }}>
                ARIA · CINEMATIC PROMO · 9:16
            </div>
            <h1 style={{ fontSize: "clamp(30px, 5vw, 52px)", margin: "0 0 16px", letterSpacing: "-0.04em", fontWeight: 200, lineHeight: 1.05, fontFamily: "Geist, sans-serif" }}>
                Una coscienza<br />che emerge dalla luce.
            </h1>
            <p style={{ opacity: 0.6, marginBottom: 36, fontSize: 15, lineHeight: 1.5, fontFamily: "Outfit, sans-serif" }}>
                30 secondi. Verticale 9:16.<br />
                Registra lo schermo (Loom / QuickTime / OBS), inquadra solo lo stage.
            </p>
            <button onClick={onStart} data-testid="promo-start" style={{
                padding: "16px 52px", borderRadius: 999,
                background: "linear-gradient(135deg,#4F8BFF,#2B5AFF)",
                color: "white", border: 0, fontSize: 17, fontWeight: 600,
                cursor: "pointer", boxShadow: "0 20px 60px -16px rgba(79,139,255,0.8)",
                letterSpacing: "0.02em", fontFamily: "Geist, sans-serif",
            }}>
                ▶ Avvia (30s)
            </button>
        </div>
    );
}

/* =====================================================================
 * AriaParticleCanvas — Three.js scene with custom GLSL shaders.
 * ===================================================================== */
function AriaParticleCanvas({ t }) {
    const containerRef = useRef(null);
    const ariaRef = useRef(null); // holds Three.js scene state
    const tRef = useRef(t);
    useEffect(() => { tRef.current = t; }, [t]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const aria = createAriaScene(container);
        ariaRef.current = aria;

        // Sample face image into point cloud
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
            const points = sampleFacePoints(img, 11000);
            aria.setFacePoints(points);
        };
        img.src = FACE_SRC;

        // rAF render loop
        let raf;
        const start = performance.now();
        let last = start;
        const loop = () => {
            const now = performance.now();
            const dt = (now - last) / 1000;
            last = now;
            aria.update(tRef.current, dt);
            aria.render();
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);

        // Resize handler
        const ro = new ResizeObserver(() => aria.resize());
        ro.observe(container);

        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
            aria.dispose();
        };
    }, []);

    return <div ref={containerRef}
        data-testid="aria-particle-canvas"
        style={{ position: "absolute", inset: 0, zIndex: 1, pointerEvents: "none" }} />;
}

/* =====================================================================
 * Three.js scene factory — encapsulates all imperative GL state.
 * ===================================================================== */
function createAriaScene(container) {
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
    camera.position.set(0, 0, 4.5);

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    container.appendChild(renderer.domElement);
    renderer.domElement.style.position = "absolute";
    renderer.domElement.style.inset = "0";
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";

    /* Uniforms */
    const uniforms = {
        uTime: { value: 0 },
        uEmerge: { value: 0 },        // 0..1 emergence progress
        uBreath: { value: 1 },        // scale 1.0..1.02
        uDissolve: { value: 0 },      // 0..1 outward dispersion
        uOpacity: { value: 1 },       // global alpha
        uPixelRatio: { value: renderer.getPixelRatio() },
    };

    /* Geometry — empty initially; populated when face image samples arrive */
    const geom = new THREE.BufferGeometry();
    const positions = new Float32Array(0);
    geom.setAttribute("position", new THREE.BufferAttribute(positions, 3));

    /* Material */
    const material = new THREE.ShaderMaterial({
        uniforms,
        vertexShader,
        fragmentShader,
        transparent: true,
        depthWrite: false,
        depthTest: false,
        blending: THREE.AdditiveBlending,
    });

    const points = new THREE.Points(geom, material);
    scene.add(points);

    /* Group for rotation (so we don't rotate camera) */
    const group = new THREE.Group();
    group.add(points);
    // Wait — we already added points to scene. Re-add to group instead.
    scene.remove(points);
    group.add(points);
    scene.add(group);

    function resize() {
        const w = container.clientWidth;
        const h = container.clientHeight;
        if (w === 0 || h === 0) return;
        renderer.setSize(w, h, false);
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
    }
    resize();

    function setFacePoints(cloud) {
        // cloud = { positions: Float32Array, random: Float32Array, idx: Float32Array, hair: Float32Array<bool> }
        geom.setAttribute("position", new THREE.BufferAttribute(cloud.positions, 3));
        geom.setAttribute("aRandom", new THREE.BufferAttribute(cloud.random, 1));
        geom.setAttribute("aIndex", new THREE.BufferAttribute(cloud.idx, 1));
        geom.setAttribute("aHair", new THREE.BufferAttribute(cloud.hair, 1));
        geom.setAttribute("aHomeZ", new THREE.BufferAttribute(cloud.homeZ, 1));
        geom.computeBoundingSphere();
    }

    function update(t, dt) {
        uniforms.uTime.value = t;

        // Phase-driven uniforms — NESSUNA dissolvenza, il viso resta SEMPRE visibile
        let emerge = 0, opacity = 1;
        let rotY = 0, camZ = 4.5;

        if (t < TL.emerge[1]) {
            // Emergenza: particelle convergono nel volto
            const k = Math.min(1, t / TL.emerge[1]);
            emerge = easeOut(k);
            opacity = k * 0.95;
        } else if (t < TL.big[1]) {
            // Frontale, full opacity per accompagnare i primi testi e "ARIA È OVUNQUE"
            emerge = 1; opacity = 1;
        } else if (t < TL.line7[1]) {
            // Aria ascolta / comprende / connette → lievi rotazioni sinistra → centro → destra
            emerge = 1; opacity = 1;
            const span = TL.line7[1] - TL.big[1];
            const k = (t - TL.big[1]) / span;            // 0..1 sui 3 verbi
            // sin curve: 0 → -1 (ascolta, sx) → 0 (comprende) → +1 (connette, dx)
            rotY = Math.sin(k * Math.PI * 1.0 - Math.PI / 2) * (14 * Math.PI / 180);
        } else if (t < TL.line9[1]) {
            // Internet vs Aria + "Ogni installazione…" → ritorno frontale, respiro più ampio
            emerge = 1; opacity = 1;
            const k = (t - TL.line7[1]) / (TL.line9[1] - TL.line7[1]);
            rotY = lerp(14 * Math.PI / 180, 0, smoothstep(0, 1, k));
        } else if (t < TL.cta[1]) {
            // CTA: il viso RESTA visibile (no dissolve), respira più lentamente,
            // leggero zoom in per intimità, opacità soffusa per non disturbare il testo
            const k = (t - TL.cta[0]) / (TL.cta[1] - TL.cta[0]);
            camZ = lerp(4.5, 3.9, easeOut(Math.min(1, k * 1.4)));
            emerge = 1;
            // Respiro lento di base 0.55 ± 0.10 — visibile ma non invadente
            opacity = 0.55 + Math.sin(t * 0.9) * 0.10;
            rotY = Math.sin(t * 0.25) * (3 * Math.PI / 180);
        } else {
            // Dopo la fine: continua a respirare
            emerge = 1;
            opacity = 0.55 + Math.sin(t * 0.9) * 0.10;
            camZ = 3.9;
        }

        // Breathing scale (ampio ma soffice) — un po' più marcato in CTA per leggibilità del "respiro"
        const breathAmp = t >= TL.cta[0] ? 0.030 : 0.018;
        const breathFreq = t >= TL.cta[0] ? 0.85 : 1.25;
        const scale = 1 + Math.sin(t * breathFreq) * breathAmp;

        // Floating motion (continuous)
        const floatX = Math.sin(t * 0.6) * 0.04;
        const floatY = Math.sin(t * 0.4 + 1.3) * 0.06;
        const floatZ = Math.sin(t * 0.5) * 0.03;

        group.rotation.y = rotY;
        group.position.set(floatX, floatY, floatZ);
        group.scale.setScalar(scale);
        camera.position.z = camZ;

        uniforms.uEmerge.value = emerge;
        uniforms.uOpacity.value = opacity;
        uniforms.uDissolve.value = 0;   // mai dissolvenza
        uniforms.uBreath.value = scale;
    }

    function render() { renderer.render(scene, camera); }

    function dispose() {
        geom.dispose();
        material.dispose();
        renderer.dispose();
        if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
    }

    return { setFacePoints, update, render, resize, dispose };
}

/* =====================================================================
 * Face Sampling — picks bright pixels from the face image and adds 3D depth.
 * ===================================================================== */
function sampleFacePoints(img, N) {
    const SAMPLE_W = 280;
    const SAMPLE_H = Math.round(SAMPLE_W * img.height / img.width);
    const off = document.createElement("canvas");
    off.width = SAMPLE_W; off.height = SAMPLE_H;
    const octx = off.getContext("2d", { willReadFrequently: true });
    octx.drawImage(img, 0, 0, SAMPLE_W, SAMPLE_H);
    const data = octx.getImageData(0, 0, SAMPLE_W, SAMPLE_H).data;

    // Scale: map image to world coordinates roughly fitting [-1.4, 1.4] in Y
    const worldH = 2.8;
    const worldW = worldH * (SAMPLE_W / SAMPLE_H);

    const positions = new Float32Array(N * 3);
    const random = new Float32Array(N);
    const idx = new Float32Array(N);
    const hair = new Float32Array(N);   // 1 if hair region
    const homeZ = new Float32Array(N);  // depth offset memo

    let placed = 0;
    let attempts = 0;
    const maxAttempts = N * 80;
    while (placed < N && attempts < maxAttempts) {
        attempts++;
        const px = Math.floor(Math.random() * SAMPLE_W);
        const py = Math.floor(Math.random() * SAMPLE_H);
        const i = (py * SAMPLE_W + px) * 4;
        const r = data[i], g = data[i + 1], b = data[i + 2];
        const lum = (r * 0.299 + g * 0.587 + b * 0.114) / 255;
        if (lum < 0.16) continue;
        if (Math.random() > Math.pow(lum, 0.95)) continue;

        // World position
        const nx = (px / SAMPLE_W - 0.5) * worldW;
        const ny = -(py / SAMPLE_H - 0.5) * worldH;

        // Depth (z): central face slightly forward, hair recedes
        // Use distance from face center (image x≈0.5, y≈0.40)
        const dx = (px / SAMPLE_W) - 0.5;
        const dy = (py / SAMPLE_H) - 0.40;
        const distCenter = Math.hypot(dx, dy * 1.2);
        // Center of face: z ~ +0.25, edges/hair: z ~ -0.15
        const z = Math.cos(Math.min(1, distCenter * 2.4) * Math.PI / 2) * 0.40 - 0.10;
        // Small randomness for organic depth
        const zJitter = (Math.random() - 0.5) * 0.06;

        // Hair detection: outside central oval AND lum>0.2 → hair
        const ovalDist = Math.hypot(dx / 0.20, (dy + 0.05) / 0.30);
        const isHair = ovalDist > 1 ? 1 : 0;

        positions[placed * 3] = nx;
        positions[placed * 3 + 1] = ny;
        positions[placed * 3 + 2] = z + zJitter;
        random[placed] = Math.random();
        // index for stagger dissolution: distance from center (so outer dissolves first)
        idx[placed] = Math.min(1, distCenter * 1.5);
        hair[placed] = isHair;
        homeZ[placed] = z + zJitter;
        placed++;
    }

    return { positions, random, idx, hair, homeZ };
}

/* =====================================================================
 * GLSL Shaders                                                          */
/* ===================================================================== */
const vertexShader = /* glsl */ `
attribute float aRandom;
attribute float aIndex;
attribute float aHair;
attribute float aHomeZ;

uniform float uTime;
uniform float uEmerge;
uniform float uDissolve;
uniform float uOpacity;
uniform float uPixelRatio;

varying float vAlpha;
varying float vFlicker;

void main() {
    vec3 pos = position;

    // EMERGENCE: scatter particles outward when uEmerge=0, gather when uEmerge=1.
    // The "from" position is a scattered cloud based on aRandom.
    vec3 scatter = vec3(
        (aRandom - 0.5) * 8.0,
        (fract(aRandom * 17.3) - 0.5) * 12.0,
        (fract(aRandom * 31.7) - 0.5) * 6.0
    );
    float emergeK = smoothstep(0.0, 1.0, uEmerge);
    pos = mix(scatter, pos, emergeK);

    // DISSOLUTION: outer particles fly outward first, inner last.
    if (uDissolve > 0.0) {
        float threshold = uDissolve * 1.5; // > aIndex means dissolving
        float dissolveK = clamp(threshold - aIndex, 0.0, 1.0);
        // Direction: away from origin
        vec3 dir = normalize(pos + vec3(0.001));
        pos += dir * dissolveK * 2.5 * (0.6 + aRandom * 0.4);
    }

    // FLOATING: each particle drifts on a sinusoid based on its depth
    float depthFactor = (aHomeZ + 0.5);
    pos.x += sin(uTime * 0.7 + aRandom * 6.28) * 0.012 * depthFactor;
    pos.y += sin(uTime * 0.5 + aRandom * 9.31) * 0.018 * depthFactor;
    pos.z += sin(uTime * 0.6 + aRandom * 13.0) * 0.010 * depthFactor;

    // HAIR WIND: hair particles get extra horizontal wind
    if (aHair > 0.5) {
        pos.x += sin(uTime * 0.9 + pos.y * 2.0 + aRandom * 4.0) * 0.06;
        pos.y -= 0.005 * sin(uTime * 0.4 + aRandom * 3.0);
    }

    vec4 mvPos = modelViewMatrix * vec4(pos, 1.0);
    gl_Position = projectionMatrix * mvPos;

    // Point size: TINY fine particles for delicate facial readability
    float baseSize = mix(0.40, 0.95, depthFactor);
    if (aHair > 0.5) baseSize *= 0.78;
    gl_PointSize = baseSize * uPixelRatio * (16.0 / -mvPos.z);

    // Per-particle flicker
    vFlicker = 0.7 + 0.30 * sin(uTime * 4.0 + aRandom * 28.0);

    // Alpha: fade outer particles during dissolution
    float dissolveFade = 1.0;
    if (uDissolve > 0.0) {
        dissolveFade = max(0.15, 1.0 - clamp(uDissolve * 1.5 - aIndex, 0.0, 1.0) * 0.7);
    }
    vAlpha = uOpacity * dissolveFade * (0.42 + 0.55 * emergeK);
}
`;

const fragmentShader = /* glsl */ `
precision mediump float;
varying float vAlpha;
varying float vFlicker;

void main() {
    // Soft circular sprite, tight falloff for fine point look
    vec2 c = gl_PointCoord - 0.5;
    float d = length(c);
    if (d > 0.5) discard;
    // Sharper radial falloff
    float a = smoothstep(0.5, 0.0, d);
    a = pow(a, 2.4);
    vec3 col = vec3(1.0) * vFlicker;
    gl_FragColor = vec4(col, a * vAlpha);
}
`;

function easeOut(x) { return 1 - Math.pow(1 - x, 3); }
function lerp(a, b, k) { return a + (b - a) * k; }
function smoothstep(a, b, x) { const t = Math.max(0, Math.min(1, (x - a) / (b - a))); return t * t * (3 - 2 * t); }

/* =====================================================================
 * TextLayer — frasi della Home, line-by-line, in dissolvenza sopra il viso
 * ===================================================================== */
function TextLayer({ t }) {
    const big = "10cqw";
    const med = "6.2cqw";
    const small = "5.4cqw";
    return (
        <div style={{ position: "absolute", inset: 0, zIndex: 5, pointerEvents: "none" }}>
            {/* ATTO 1 — c'è qualcosa che sta cambiando */}
            <PromptLine show={inRange(t, TL.line1)} fontSize={med} y="50%" wrap multi>
                <>C&apos;è qualcosa<br /><span style={{ color: "#9EC3FF" }}>che sta cambiando</span>.</>
            </PromptLine>

            {/* ATTO 2 — non è un'app / non è un chatbot / è una presenza */}
            <PromptLine show={inRange(t, TL.line2)} fontSize={small} y="80%" dim text="Non è un'app." />
            <PromptLine show={inRange(t, TL.line3)} fontSize={small} y="80%" dim text="Non è un chatbot." />
            <PromptLine show={inRange(t, TL.line4)} fontSize={small} y="80%" accent text="È una presenza." />

            {/* ATTO 3 — ARIA È OVUNQUE */}
            <BigLine show={inRange(t, TL.big)} fontSize={big} y="50%" />

            {/* ATTO 5 — ascolta / comprende / connette */}
            <PromptLine show={inRange(t, TL.line5)} fontSize={small} y="80%">
                <>Aria <em style={accentItalic}>ascolta</em>.</>
            </PromptLine>
            <PromptLine show={inRange(t, TL.line6)} fontSize={small} y="80%">
                <>Aria <em style={accentItalic}>comprende</em>.</>
            </PromptLine>
            <PromptLine show={inRange(t, TL.line7)} fontSize={small} y="80%">
                <>Aria <em style={accentItalic}>connette</em>.</>
            </PromptLine>

            {/* ATTO 10 — internet vs aria */}
            <PromptLine show={inRange(t, TL.line8)} fontSize={med} y="50%" wrap multi>
                <>Internet collega <span style={{ color: "rgba(255,255,255,0.4)" }}>pagine</span>.<br />
                Aria collega <span style={{ color: "#9EC3FF" }}>bisogni</span>.</>
            </PromptLine>

            {/* ATTO 8 — ogni installazione… */}
            <PromptLine show={inRange(t, TL.line9)} fontSize={med} y="50%" wrap multi>
                <>Ogni installazione<br />la rende <em style={accentItalic}>più intelligente</em>.</>
            </PromptLine>
        </div>
    );
}

const accentItalic = {
    fontStyle: "normal",
    color: "#9EC3FF",
    background: "linear-gradient(180deg, #FFFFFF 0%, #9EC3FF 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
};

function inRange(t, [a, b]) { return t >= a && t < b; }

/* PromptLine — fade-in pulito con blinking cursor. Supporta children JSX,
 * varianti dim/accent, e modalità multi (multi-line, niente cursore). */
function PromptLine({ show, text, children, fontSize, y, wrap, multi, dim, accent }) {
    const color = accent ? "#9EC3FF" : (dim ? "rgba(255,255,255,0.55)" : "white");
    return (
        <AnimatePresence>
            {show && (
                <div style={{
                    position: "absolute", left: "50%", top: y,
                    transform: "translate(-50%, -50%)",
                    width: "92%", textAlign: "center", pointerEvents: "none",
                }}>
                    <motion.div
                        initial={{ opacity: 0, y: 14, filter: "blur(8px)" }}
                        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        exit={{ opacity: 0, y: -10, filter: "blur(8px)" }}
                        transition={{ duration: 0.7, ease: easeCurve }}
                        style={{
                            fontSize, fontWeight: 200, color,
                            letterSpacing: "-0.035em", textAlign: "center",
                            fontFamily: "Geist, sans-serif",
                            textShadow: "0 6px 40px rgba(0,0,0,0.95), 0 0 60px rgba(0,0,0,0.6)",
                            lineHeight: multi ? 1.08 : 1.2,
                            whiteSpace: (wrap || multi) ? "normal" : "nowrap",
                            display: "inline-block",
                        }}>
                        <span>{children ?? text}</span>
                        {!multi && (
                            <span style={{
                                display: "inline-block",
                                width: "0.06em", marginLeft: "0.08em",
                                height: "0.85em",
                                background: "#9EC3FF",
                                animation: "ariaCursor 0.85s steps(2) infinite",
                                verticalAlign: "-0.08em",
                            }} />
                        )}
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}

/* Big hero text "ARIA È OVUNQUE." — drammatico scale-in, gradiente come in Home */
function BigLine({ show, fontSize, y }) {
    return (
        <AnimatePresence>
            {show && (
                <div style={{
                    position: "absolute", left: "50%", top: y,
                    transform: "translate(-50%, -50%)",
                    width: "96%", textAlign: "center", pointerEvents: "none",
                }}>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.85, filter: "blur(16px)" }}
                        animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                        exit={{ opacity: 0, scale: 1.08, filter: "blur(12px)" }}
                        transition={{ duration: 1.0, ease: easeCurve }}
                        style={{
                            fontSize, fontWeight: 200,
                            letterSpacing: "-0.06em", textAlign: "center",
                            fontFamily: "Geist, sans-serif",
                            lineHeight: 0.9,
                            background: "linear-gradient(180deg, #FFFFFF 0%, #9EC3FF 65%, rgba(158,195,255,0.3) 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            textShadow: "0 0 60px rgba(79,139,255,0.45)",
                            display: "inline-block",
                        }}>
                        ARIA È<br />OVUNQUE.
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}

/* Inject cursor blink + typewriter keyframes */
const cursorStyle = document.createElement("style");
cursorStyle.textContent = `
@keyframes ariaCursor { 0%, 100% { opacity: 1 } 50% { opacity: 0 } }
@keyframes ariaTypeIn { from { clip-path: inset(0 100% 0 0); } to { clip-path: inset(0 0 0 0); } }
`;
if (typeof document !== "undefined" && !document.getElementById("aria-cursor-kf")) {
    cursorStyle.id = "aria-cursor-kf";
    document.head.appendChild(cursorStyle);
}

/* =====================================================================
 * CTAScene — voice + 3-phase tagline + logo
 * ===================================================================== */
function CTAScene({ t }) {
    // Map global outro time to absolute timeline checks
    const tt = t + TL.cta[0];
    const showPrimary = inRange(tt, TL.ctaA);
    return (
        <div style={{
            position: "absolute", inset: 0, zIndex: 7,
            display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center",
            background: "radial-gradient(ellipse at center, rgba(5,9,19,0.10) 30%, rgba(0,0,0,0.55) 100%)",
            gap: "2.5cqh", padding: "6%", pointerEvents: "none",
        }}>
            {/* Phase A: primary CTA with voice */}
            <AnimatePresence>
                {showPrimary && (
                    <div style={{
                        position: "absolute",
                        top: "44%", left: "50%", transform: "translate(-50%, -50%)",
                        width: "88%", textAlign: "center", pointerEvents: "none",
                    }}>
                        <motion.div
                            initial={{ opacity: 0, y: 18, filter: "blur(12px)" }}
                            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                            exit={{ opacity: 0, y: -12, filter: "blur(10px)" }}
                            transition={{ duration: 0.85, ease: easeCurve }}
                            style={{
                                fontSize: "6.4cqw", fontWeight: 200, letterSpacing: "-0.035em",
                                textAlign: "center", lineHeight: 1.15,
                                fontFamily: "Geist, sans-serif",
                                textShadow: "0 6px 40px rgba(0,0,0,0.9)",
                                display: "inline-block",
                            }}>
                            Installami sul tuo sito.<br />
                            <span style={{ color: "#9EC3FF" }}>Bastano 5 minuti.</span>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Phase B: 3-phase outro tagline (after primary exits) */}
            <div style={{ position: "absolute", top: "44%", left: 0, right: 0, transform: "translateY(-50%)", height: "12cqw" }}>
                <OutroWord show={inRange(tt, TL.cta1)} text="ENTRA" big />
                <OutroWord show={inRange(tt, TL.cta2)} text="nell'ecosistema di imprese" />
                <OutroWord show={inRange(tt, TL.cta3)} text="interconnesse con Aria" />
            </div>

            {/* Logo + totalklick.it: persistent throughout CTA, smaller */}
            <div style={{
                position: "absolute", bottom: "16cqh", left: "50%", transform: "translateX(-50%)",
                width: "28cqw", textAlign: "center", pointerEvents: "none",
            }}>
                <motion.img src="/brand/totalklick-logo.svg" alt="TotalKlick"
                    initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.4, ease: easeCurve }}
                    style={{
                        width: "100%", display: "block",
                        filter: "drop-shadow(0 0 50px rgba(79,139,255,0.55))",
                    }} />
            </div>
            <div style={{
                position: "absolute", bottom: "11cqh", left: "50%", transform: "translateX(-50%)",
                textAlign: "center", pointerEvents: "none",
            }}>
                <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.9 }}
                    style={{
                        fontSize: "2.6cqw", color: "rgba(255,255,255,0.7)",
                        letterSpacing: "0.22em", textTransform: "uppercase",
                        fontFamily: "JetBrains Mono, monospace",
                        whiteSpace: "nowrap",
                    }}>
                    totalklick.it
                </motion.div>
            </div>
        </div>
    );
}

function OutroWord({ show, text, big }) {
    return (
        <AnimatePresence>
            {show && (
                <div style={{
                    position: "absolute", left: 0, right: 0, top: "50%",
                    transform: "translateY(-50%)",
                    textAlign: "center", pointerEvents: "none",
                }}>
                    <motion.div
                        initial={{ opacity: 0, y: 18, filter: "blur(14px)" }}
                        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        exit={{ opacity: 0, y: -14, filter: "blur(10px)" }}
                        transition={{ duration: 0.55, ease: easeCurve }}
                        style={{
                            fontSize: big ? "8.5cqw" : "4.6cqw",
                            fontWeight: big ? 300 : 200,
                            color: big ? "#9EC3FF" : "white",
                            letterSpacing: big ? "0.1em" : "-0.02em",
                            fontFamily: "Geist, sans-serif",
                            textShadow: big
                                ? "0 0 70px rgba(79,139,255,0.8), 0 0 140px rgba(79,139,255,0.45)"
                                : "0 6px 30px rgba(0,0,0,0.9)",
                            textTransform: big ? "uppercase" : "none",
                            whiteSpace: "nowrap",
                            display: "inline-block",
                        }}>
                        {text}
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}

/* ===================================================================== */
function Vignette() {
    return <div style={{
        position: "absolute", inset: 0, zIndex: 8, pointerEvents: "none",
        background: "radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.85) 100%)",
    }} />;
}

function TimecodeHUD({ t }) {
    const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(Math.floor(s % 60)).padStart(2, "0")}.${String(Math.floor((s * 10) % 10))}`;
    return (
        <div style={{
            position: "absolute", bottom: 12, left: 14, zIndex: 9,
            fontFamily: "JetBrains Mono, monospace", fontSize: 10, letterSpacing: "0.2em",
            color: "rgba(255,255,255,0.45)", padding: "4px 10px",
            border: "1px solid rgba(255,255,255,0.1)", borderRadius: 4,
            background: "rgba(0,0,0,0.5)", backdropFilter: "blur(6px)",
        }} data-testid="promo-timecode">
            ● REC · {fmt(t)} / {fmt(TL.total)}
        </div>
    );
}
